<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Live phone mockup. Renders the path the customer walked as a chat in the
// selected channel's native style; tapping a linked button navigates forward.
const props = defineProps({
  screens: { type: Array, default: () => [] },
  path: { type: Array, default: () => [] }, // array of screen ids
  channel: { type: String, default: 'whatsapp' }, // whatsapp | fb | instagram | store
  mode: { type: String, default: 'day' },
  noEntryButtons: { type: Boolean, default: false },
});
const emit = defineEmits(['navigate']);
const { t } = useI18n();

// Show the "customer is stuck here" note when the last shown bubble is the
// greeting itself and it carries no reply buttons.
const stuckAtGreeting = computed(() => {
  if (!props.noEntryButtons) return false;
  return props.path[props.path.length - 1] === 'greeting';
});

const byId = computed(() =>
  props.screens.reduce((acc, s) => {
    acc[s.id] = s;
    return acc;
  }, {})
);

const effectiveText = screen => {
  if (!screen) return '';
  // At night an off-hours screen shows only its off-hours line (for the root
  // greeting that means the notice replaces the daytime welcome body); the
  // reply buttons still render separately.
  return props.mode === 'night' && screen.offHours
    ? screen.offHours
    : screen.text;
};

// Each step = a bot bubble, optionally preceded by the customer's tap.
const steps = computed(() =>
  props.path
    .map((id, idx) => {
      const screen = byId.value[id];
      if (!screen) return null;
      let tappedLabel = null;
      if (idx > 0) {
        const prev = byId.value[props.path[idx - 1]];
        const tapped = (prev?.buttons || []).find(b => b.target === id);
        tappedLabel = tapped ? tapped.label : null;
      }
      return { screen, tappedLabel, isLast: idx === props.path.length - 1 };
    })
    .filter(Boolean)
);

const lastButtons = computed(() => {
  const last = props.path[props.path.length - 1];
  const screen = byId.value[last];
  return (screen?.buttons || []).filter(b => (b.label || '').trim());
});

const tap = btn => {
  if (btn.target) emit('navigate', btn.target);
};

const escapeHtml = s =>
  (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const format = txt =>
  escapeHtml(txt)
    .replace(/\*(.+?)\*/g, '<b>$1</b>')
    .replace(/\n/g, '<br>');

const headerClass = {
  whatsapp: 'bg-[#075e54]',
  fb: 'bg-[#0a7cff]',
  instagram: 'bg-gradient-to-l from-[#7d2ae8] via-[#dc2743] to-[#f09433]',
  store: 'bg-[#1f2d3d]',
};
const bodyClass = {
  whatsapp: 'bg-[#efeae2]',
  fb: 'bg-white',
  instagram: 'bg-white',
  store: 'bg-[#f4f6fb]',
};
const botBubbleClass = {
  whatsapp: 'bg-white text-[#111b21]',
  fb: 'bg-[#f0f2f5] text-[#050505]',
  instagram: 'bg-[#efefef] text-black',
  store: 'bg-white text-[#1f2d3d] shadow-sm',
};
const meBubbleClass = {
  whatsapp: 'bg-[#d9fdd3] text-[#111b21]',
  fb: 'bg-[#0a7cff] text-white',
  instagram: 'bg-[#efefef] text-black',
  store: 'bg-[#1f93ff] text-white',
};
const sendClass = {
  whatsapp: 'bg-[#00a884]',
  fb: 'bg-[#0a7cff]',
  instagram: 'bg-[#0095f6]',
  store: 'bg-[#1f93ff]',
};
const statusText = computed(() => {
  if (props.channel === 'fb')
    return t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.STATUS_FB');
  if (props.channel === 'instagram')
    return t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.STATUS_IG');
  if (props.channel === 'store')
    return t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.STATUS_STORE');
  return t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.STATUS_WA');
});
</script>

<template>
  <div class="relative rounded-[38px] bg-[#0c1020] p-3 shadow-2xl">
    <div
      class="absolute left-1/2 top-3 z-[5] h-6 w-28 -translate-x-1/2 rounded-b-2xl bg-[#0c1020]"
    />
    <div
      class="flex h-[600px] flex-col overflow-hidden rounded-[30px] bg-white"
    >
      <!-- chat header -->
      <div
        class="relative z-[2] flex items-center gap-3 px-4 pb-3 pt-9 text-white"
        :class="headerClass[channel]"
      >
        <div
          class="grid size-9 place-items-center rounded-full bg-white/25 text-[15px] font-extrabold"
        >
          {{ t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.AVATAR') }}
        </div>
        <div>
          <div class="text-[14.5px] font-bold">
            {{ t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.PAGE_NAME') }}
          </div>
          <div class="text-[11px] opacity-85">{{ statusText }}</div>
        </div>
        <span
          v-if="mode === 'night'"
          class="ms-auto flex items-center gap-1 rounded-full bg-black/25 px-2.5 py-1 text-[10.5px] font-extrabold"
        >
          <span class="i-lucide-moon size-3" />
          {{ t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.NIGHT_BADGE') }}
        </span>
      </div>

      <!-- chat body -->
      <div
        class="flex flex-1 flex-col gap-2 overflow-y-auto p-3"
        :class="bodyClass[channel]"
      >
        <template v-for="(step, idx) in steps" :key="idx">
          <!-- customer tap -->
          <div
            v-if="step.tappedLabel"
            class="max-w-[84%] self-end rounded-2xl rounded-tl-md px-3 py-2 text-[13px] shadow-sm"
            :class="meBubbleClass[channel]"
          >
            {{ step.tappedLabel }}
          </div>
          <!-- bot bubble -->
          <div
            class="max-w-[84%] self-start whitespace-pre-wrap break-words rounded-2xl rounded-tr-md px-3 py-2 text-[13px] leading-[1.75] shadow-sm"
            :class="botBubbleClass[channel]"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="format(effectiveText(step.screen))" />
          </div>

          <!-- buttons under the last bubble -->
          <template v-if="step.isLast && lastButtons.length">
            <!-- WhatsApp interactive buttons -->
            <div
              v-if="channel === 'whatsapp'"
              class="max-w-[84%] self-start overflow-hidden rounded-xl bg-white shadow-sm"
            >
              <button
                v-for="(btn, bi) in lastButtons"
                :key="bi"
                type="button"
                class="flex w-full items-center justify-center gap-1.5 border-t border-[#e9e9e9] bg-white p-2.5 text-[13px] font-bold text-[#1da5e0] transition-colors first:border-t-0 hover:bg-[#f6f6f6]"
                @click="tap(btn)"
              >
                <span
                  v-if="btn.target"
                  class="i-lucide-corner-down-left size-3.5"
                />
                {{ btn.label }}
              </button>
            </div>

            <!-- Messenger button template -->
            <div
              v-else-if="channel === 'fb'"
              class="max-w-[84%] self-start overflow-hidden rounded-2xl border border-[#e4e6eb] bg-white"
            >
              <button
                v-for="(btn, bi) in lastButtons"
                :key="bi"
                type="button"
                class="w-full border-t border-[#e4e6eb] bg-white p-3 text-[13.5px] font-bold text-[#0a7cff] transition-colors first:border-t-0 hover:bg-[#f5f8ff]"
                @click="tap(btn)"
              >
                {{ btn.label }}
              </button>
            </div>

            <!-- Store web-chat buttons -->
            <div
              v-else-if="channel === 'store'"
              class="flex max-w-[92%] flex-col gap-1.5 self-start"
            >
              <button
                v-for="(btn, bi) in lastButtons"
                :key="bi"
                type="button"
                class="flex items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-[#1f93ff] bg-white px-4 py-2.5 text-[13px] font-bold text-[#1f93ff] transition-colors hover:bg-[#eaf4ff]"
                @click="tap(btn)"
              >
                <span
                  v-if="btn.target"
                  class="i-lucide-corner-down-left size-3.5"
                />
                {{ btn.label }}
              </button>
            </div>

            <!-- Instagram quick replies -->
            <div
              v-else
              class="flex max-w-[92%] flex-wrap justify-end gap-1.5 self-end"
            >
              <button
                v-for="(btn, bi) in lastButtons"
                :key="bi"
                type="button"
                class="rounded-full border-[1.5px] border-[#0095f6] bg-white px-3.5 py-2 text-[12.5px] font-bold text-[#0095f6] transition-colors hover:bg-[#e7f3ff]"
                @click="tap(btn)"
              >
                {{ btn.label }}
              </button>
            </div>
          </template>
        </template>

        <!-- customer is stuck: greeting shown with no reply buttons -->
        <div
          v-if="stuckAtGreeting"
          class="mt-1 flex max-w-[92%] items-center gap-1.5 self-start rounded-xl border border-dashed border-[#e0736f] bg-[#fdecec] px-3 py-2 text-[11.5px] font-bold text-[#c0392b]"
        >
          <span class="i-lucide-circle-alert size-3.5 shrink-0" />
          {{ t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.NO_ENTRY_NOTE') }}
        </div>
      </div>

      <!-- chat input (cosmetic) -->
      <div
        class="flex items-center gap-2.5 border-t border-[#e4e6eb] bg-[#f0f2f5] p-2.5"
      >
        <div
          class="flex-1 rounded-full bg-white px-3.5 py-2 text-xs text-n-slate-10"
        >
          {{ t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.INPUT_PLACEHOLDER') }}
        </div>
        <div
          class="grid size-8 place-items-center rounded-full text-sm text-white"
          :class="sendClass[channel]"
        >
          <span class="i-lucide-send size-4" />
        </div>
      </div>
    </div>
  </div>
</template>
